
import pick from 'lodash/pick';

import mapObjectProperties from '../../utils/mapObjectProperties';
import { ADD_OR_UPDATE_VALUES, ADD_OR_UPDATE_SORT_ORDER } from './constants';
import objectsDiff from '../../utils/objectsDiff';
import { toString } from '../../shared/helpers';

export const $externalUpdates = Symbol('externalUpdated');

const areValuesStringEqual = (existingValue, importedValue) =>
  toString(existingValue) === toString(importedValue);

export const areValuesEqual = (existingValue, importedValue) => {
  if (typeof existingValue === typeof importedValue && typeof existingValue === 'number') {
    return existingValue.toFixed(15) === importedValue.toFixed(15);
  }
  if (!existingValue && !importedValue && (typeof existingValue !== 'number' && typeof importedValue !== 'number')) {
    return true;
  }

  return areValuesStringEqual(existingValue, importedValue);
};

const getExternalUpdatedKeys = (externalUpdates = {}) => Object.entries(externalUpdates)
  .filter(([, updated]) => updated)
  .map(([key]) => key);

export const getAddOrUpdateInfo = (
  importedEntity,
  existingEntities,
  { idField, idFieldComparator, ignoredFields = [], entitiesValuesComparator = areValuesEqual }
) => {
  const existingEntity = idFieldComparator
    ? existingEntities.find(entity => idFieldComparator(entity, importedEntity))
    : existingEntities.find(entity => areValuesStringEqual(entity[idField], importedEntity[idField]));

  if (!existingEntity) {
    return { status: ADD_OR_UPDATE_VALUES.ADD };
  }

  const keysWithErrors = importedEntity.validationData
    ? Object.keys(importedEntity.validationData)
    : [];

  const existingEntityToCompare = pick(existingEntity, Object.keys(importedEntity));
  const importedEntityToCompare = pick(importedEntity, Object.keys(existingEntity));

  const differingKeys = Object.keys(objectsDiff(
    existingEntityToCompare,
    importedEntityToCompare,
    entitiesValuesComparator
  ))
    .filter(key => !keysWithErrors.includes(key));

  const differingNotIgnoredKeys = differingKeys.filter(key => !ignoredFields.includes(key));

  const hasExternalChanges = Object.values(importedEntity[$externalUpdates] || {}).filter(Boolean).length > 0;

  return differingNotIgnoredKeys.length > 0 || hasExternalChanges
    ? {
      status: ADD_OR_UPDATE_VALUES.UPDATE,
      differingKeys: differingNotIgnoredKeys.concat(getExternalUpdatedKeys(importedEntity[$externalUpdates])),
      existingIdField: existingEntity[idField],
    }
    : { status: ADD_OR_UPDATE_VALUES.NO_CHANGE };
};

export const appendAddOrUpdateInfo = (
  importedEntities,
  existingEntities,
  { modifyEntity, idField, idFieldComparator, ignoredFields = [], entitiesValuesComparator }
) => (
  importedEntities.map(importedEntity => {
    const { status, differingKeys = [], existingIdField = null } = getAddOrUpdateInfo(
      importedEntity,
      existingEntities,
      { idField, idFieldComparator, ignoredFields, entitiesValuesComparator }
    );

    const entityValue = modifyEntity
      ? (
        mapObjectProperties(importedEntity, (key, value) => ({
          isUpdated: differingKeys.includes(key) && key !== ADD_OR_UPDATE_VALUES.FIELD,
          value,
        }))
      )
      : importedEntity;
    return {
      addOrUpdate: status,
      [idField]: existingIdField,
      ...entityValue,
      IMPORT_VALIDATION: importedEntity.IMPORT_VALIDATION,
    };
  })
);

export const getImportStats = entities => entities.reduce((stats, entity) => {
  const errors = entity.validationData ? stats.errors + 1 : stats.errors;
  const adds = entity.addOrUpdate === ADD_OR_UPDATE_VALUES.ADD ? stats.adds + 1 : stats.adds;
  const updates = entity.addOrUpdate === ADD_OR_UPDATE_VALUES.UPDATE ? stats.updates + 1 : stats.updates;
  const loaded = stats.loaded + 1;

  return { loaded, errors, adds, updates };
}, { loaded: 0, errors: 0, adds: 0, updates: 0 });

export const compareAddOrUpdate = (valA, valB) => {
  const orderA = ADD_OR_UPDATE_SORT_ORDER.indexOf(valA);
  const orderB = ADD_OR_UPDATE_SORT_ORDER.indexOf(valB);
  if (orderA === orderB) {
    return 0;
  } else {
    return orderA > orderB ? 1 : -1;
  }
};
